import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ApiError, type MapConfigView, type RiskAreaGeometryView, type RiskAreaRecordView } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, EmptyState, Field, ListTable, Notice, PageHeader, Panel, SegmentedControl, TextArea, TextInput, type Column, FormRow, SectionTitle, Cluster, Stack, Text, CapWidth, Drawer, DrawerHeader, DrawerBody, DrawerFooter, KeyValueList } from '@vello/web-theme';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import {
  maySelfApprove,
  EMPTY_DRAFT,
  SCHEDULE_DAYS,
  areaInForce,
  decideBlock,
  draftFromDefinition,
  draftToProposal,
  formatMarketInstant,
  groupRiskAreas,
  tileOverride,
  marketTimeZone,
  outlineOf,
  parseLatLng,
  riskAreaAuthority,
  riskStatusTone,
  roundPoint,
  utcToZonedLocal,
  type RiskAreaDraft,
  type RiskAreaRow,
} from '../../src/index';
import { MapCanvas, type MapArea } from '../components/map-canvas';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Operações › Áreas de risco (W9-RISK-AREAS-ALERTS, D474, ADR-0092; DevMobility C-024, I-076): an operator draws an area on
 * the map (tap to add a vertex — or type the coordinates when no tile source is configured), writes what the driver reads,
 * sets the window and the local daily hours in the market's zone, and PROPOSES it. A different operator approves it; until
 * then it marks nothing. Editing or switching an area off is a new version under the same rule. The market's approved areas
 * mark the offer a driver is shown when the pickup or the destination lies inside one — advisory, it never blocks a ride.
 *
 * The map is the platform's own basemap (D797; Integrações › Tiles do mapa only overrides it); without the market's map
 * configuration (no `ops:view`) the map area says so and the coordinate editor still works. Every colour is a token variable. Neutral: no country, zone or language is named.
 */

export function RiskAreasScreen(): ReactElement {
  const { runtime, markets, grants, me } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const authority = riskAreaAuthority(grants);

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [records, setRecords] = useState<readonly RiskAreaRecordView[] | undefined>(undefined);
  const [config, setConfig] = useState<MapConfigView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [draft, setDraft] = useState<RiskAreaDraft>(EMPTY_DRAFT);
  const [typed, setTyped] = useState('');
  const [formIssue, setFormIssue] = useState<string | undefined>(undefined);
  const [rejecting, setRejecting] = useState<RiskAreaRecordView | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // The record drawer (the Directory pattern): the area stays selected while the drawer closes.
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (error: ApiError): string => explain(error) ?? t('lmap.err.generic', { code: fail(error) }),
    [t, fail, explain],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setRecords(undefined);
      setError(undefined);
      return;
    }
    const res = await runtime.ops.riskAreas(market);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setRecords(res.value.areas);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no risk areas".
      setError(refuse(res.error));
    }
  }, [runtime, market, refuse]);

  useEffect(() => {
    setRecords(undefined);
    setDraft(EMPTY_DRAFT);
    setRejecting(undefined);
    setDrawerOpen(false);
    void load();
  }, [load]);

  useEffect(() => {
    if (market === undefined) {
      setConfig(undefined);
      return;
    }
    // The tiles are the Live map's slot (`ops:view`); a safety operator without it still gets the coordinate editor.
    void runtime.ops.mapConfig(market).then((res) => setConfig(res.ok ? res.value : undefined));
  }, [runtime, market]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  const rows = useMemo(() => groupRiskAreas(records ?? [], new Date().toISOString()), [records]);
  const nowIso = new Date().toISOString();
  const canDraw = authority.mayManage && market !== undefined;

  const mapAreas = useMemo<readonly MapArea[]>(
    () => rows.filter((r) => r.approved !== undefined).map((r) => ({ id: r.areaId, points: outlineOf((r.approved as RiskAreaRecordView).definition.geometry), colorVar: areaInForce(r, nowIso) ? 'var(--color-warning)' : 'var(--color-text-secondary)', label: (r.approved as RiskAreaRecordView).definition.description })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows],
  );

  const addPoint = (p: { readonly latitude: number; readonly longitude: number }): void => {
    setDraft((d) => (d.shape === 'polygon' ? { ...d, ring: [...d.ring, roundPoint(p)] } : { ...d, center: roundPoint(p) }));
    setFormIssue(undefined);
  };
  const addTyped = (): void => {
    const p = parseLatLng(typed);
    if (p === undefined) {
      setFormIssue('risk.form.coordinate');
      return;
    }
    addPoint(p);
    setTyped('');
  };

  const propose = async (): Promise<void> => {
    if (market === undefined) return;
    const built = draftToProposal(draft, timeZone);
    if (!built.ok) {
      setFormIssue(`risk.issue.${built.issue}`);
      return;
    }
    setBusy(true);
    setNotice(undefined);
    setFormIssue(undefined);
    const res = await runtime.ops.proposeRiskArea(market, built.input);
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('risk.done.proposed') });
      setDraft(EMPTY_DRAFT);
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  const decide = async (record: RiskAreaRecordView, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.decideRiskArea(market, record.definition.areaId, record.definition.version, { decision, ...(decision === 'REJECT' ? { reason } : {}) });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t(decision === 'APPROVE' ? 'risk.done.approved' : 'risk.done.rejected') });
      setRejecting(undefined);
      setReason('');
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  const edit = (row: RiskAreaRow): void => {
    const base = (row.pending ?? row.approved ?? row.latest).definition;
    setDraft(draftFromDefinition(base, (utc) => (timeZone !== undefined ? utcToZonedLocal(utc, timeZone) : undefined)));
    setFormIssue(undefined);
    setNotice(undefined);
    setDrawerOpen(false);
  };

  const columns: readonly Column<RiskAreaRow>[] = [
    {
      key: 'what',
      header: t('risk.col.area'),
      width: 'minmax(220px, 1.6fr)',
      render: (r) => (
        <span>
          <strong>{r.latest.definition.description}</strong>
          <br />
          <Text role="caption" tone="secondary">{v('geo.shape_kind', (r.latest.definition.geometry as { kind: string }).kind === 'circle' ? 'circle' : 'polygon')} · {t('risk.versions', { value: r.versions })}</Text>
        </span>
      ),
    },
    {
      key: 'state',
      header: t('risk.col.state'),
      width: 'minmax(190px, 1.2fr)',
      render: (r) => (
        <Cluster gap={2} wrap>
          {r.approved !== undefined ? (
            <Badge tone={areaInForce(r, nowIso) ? 'success' : 'neutral'}>{t(areaInForce(r, nowIso) ? 'risk.state.inForce' : r.approved.definition.active ? 'risk.state.outOfWindow' : 'risk.state.off')}</Badge>
          ) : (
            <Badge tone="neutral">{t('risk.state.notApproved')}</Badge>
          )}
          {r.pending !== undefined && <Badge tone={riskStatusTone('PROPOSED')}>{t('risk.state.pending', { value: r.pending.definition.version })}</Badge>}
          {r.pending === undefined && r.latest.status === 'REJECTED' && <Badge tone={riskStatusTone('REJECTED')}>{t('risk.state.rejected', { value: r.latest.definition.version })}</Badge>}
        </Cluster>
      ),
    },
    {
      key: 'window',
      header: t('risk.col.window'),
      width: 'minmax(210px, 1.3fr)',
      render: (r) => {
        const d = (r.approved ?? r.latest).definition;
        return (
          <Text role="caption" tone="secondary" mono>
            {formatMarketInstant(d.effectiveFrom, timeZone, lang)} → {d.effectiveTo !== undefined ? formatMarketInstant(d.effectiveTo, timeZone, lang) : t('risk.openEnded')}
            {d.schedule?.windows[0] !== undefined ? ` · ${d.schedule.windows[0].start}–${d.schedule.windows[0].end}` : ''}
          </Text>
        );
      },
    },
    {
      key: 'trail',
      header: t('risk.col.trail'),
      width: 'minmax(190px, 1.2fr)',
      render: (r) => {
        const rec = r.pending ?? r.approved ?? r.latest;
        return (
          <Text role="caption" tone="secondary">
            <PersonName id={rec.proposedBy} />
            {rec.decidedBy !== undefined && (
              <>
                {' → '}
                <PersonName id={rec.decidedBy} />
              </>
            )}
          </Text>
        );
      },
    },
  ];

  const rowActions = (r: RiskAreaRow): ReactElement => {
    const pending = r.pending;
    const block = pending !== undefined ? decideBlock(pending, me?.id, authority.mayManage, selfApproval) : undefined;
    return (
      <Cluster gap={2} wrap>
        {authority.mayManage && pending === undefined && <Button variant="secondary" disabled={busy} onClick={() => edit(r)}>{t('risk.action.edit')}</Button>}
        {pending !== undefined && block === undefined && (
          <>
            <Button variant="secondary" disabled={busy} onClick={() => void decide(pending, 'APPROVE')}>{t('risk.action.approve')}</Button>
            <Button variant="ghost" disabled={busy} onClick={() => startReject(r.areaId, pending)}>{t('risk.action.reject')}</Button>
          </>
        )}
        {pending !== undefined && block === 'OWN_PROPOSAL' && <Text role="caption" tone="secondary">{t('risk.waitOther')}</Text>}
      </Cluster>
    );
  };

  const openRecord = (areaId: string): void => {
    setOpenId(areaId);
    setDrawerOpen(true);
    setRejecting(undefined);
    setReason('');
  };
  const closeDrawer = (): void => {
    setDrawerOpen(false);
    setRejecting(undefined);
    setReason('');
  };
  const startReject = (areaId: string, pending: RiskAreaRecordView): void => {
    setOpenId(areaId);
    setDrawerOpen(true);
    setRejecting(pending);
    setReason('');
    setNotice(undefined);
  };
  const drawerRow = rows.find((r) => r.areaId === openId);

  const draftOutline = draft.shape === 'polygon' ? draft.ring : draft.center !== undefined ? outlineOf({ kind: 'circle', center: draft.center, radiusMeters: Number(draft.radiusMeters) > 0 ? Number(draft.radiusMeters) : 0 }) : [];
  const set = <K extends keyof RiskAreaDraft>(key: K, value: RiskAreaDraft[K]): void => setDraft((d) => ({ ...d, [key]: value }));
  const issueText = formIssue !== undefined ? t(formIssue) : undefined;

  return (
    <>
      <PageHeader
        title={t('risk.title')}
        subtitle={t('risk.subtitle')}
      />

      {!authority.mayManage && (
        <Notice tone="neutral">{t('risk.readOnly')}</Notice>
      )}

      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          <ListTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.areaId}
            onRowClick={(r) => openRecord(r.areaId)}
            selectedKey={drawerOpen ? openId : undefined}
            actions={rowActions}
            loading={records === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('risk.empty.failed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('risk.empty.title')} detail={t('risk.empty.detail')} />}
            minWidth={1100}
          />

          {canDraw && (
            <Panel>
              <Stack gap={4}>
                <SectionTitle>{t(draft.areaId !== undefined ? 'risk.form.titleEdit' : 'risk.form.title')}</SectionTitle>
                <Text as="p" role="caption" tone="secondary">{t('risk.form.fourEyes')}</Text>
                <CapWidth size="xl"><Stack gap={4}>
                  <Field label={t('risk.form.description')} htmlFor="risk-description" hint={t('risk.form.descriptionHint')}>
                    <TextInput id="risk-description" value={draft.description} maxLength={120} onChange={(e) => set('description', e.target.value)} />
                  </Field>

                  <SegmentedControl<'polygon' | 'circle'>
                    ariaLabel={t('risk.form.shape')}
                    value={draft.shape}
                    onChange={(v) => set('shape', v)}
                    options={[{ id: 'polygon', label: v('geo.shape_kind', 'polygon') }, { id: 'circle', label: v('geo.shape_kind', 'circle') }]}
                  />

                  {config !== undefined ? (
                    <>
                      <Text as="p" role="caption" tone="secondary">{t(draft.shape === 'polygon' ? 'risk.map.hintPolygon' : 'risk.map.hintCircle')}</Text>
                      <MapCanvas
                        tiles={tileOverride(config.tiles)}
                        center={config.market.center}
                        points={[]}
                        areas={mapAreas}
                        draft={draftOutline}
                        onMapTap={addPoint}
                        ariaLabel={t('risk.map.label')}
                        pointLabel={() => ''}
                      />
                    </>
                  ) : (
                    <Notice tone="neutral">{t('risk.map.noTiles')}</Notice>
                  )}

                  <Stack gap={3}>
                    <FormRow
                      columns="minmax(0, 360px)"
                      action={
                        <>
                          <Button variant="secondary" onClick={addTyped}>{t(draft.shape === 'polygon' ? 'risk.form.addPointAction' : 'risk.form.setCenter')}</Button>
                          {draft.shape === 'polygon' && draft.ring.length > 0 && <Button variant="ghost" onClick={() => set('ring', draft.ring.slice(0, -1))}>{t('risk.form.undoPoint')}</Button>}
                        </>
                      }
                    >
                      <Field label={t(draft.shape === 'polygon' ? 'risk.form.addPoint' : 'risk.form.center')} htmlFor="risk-point" hint={t('risk.form.pointHint')}>
                        <TextInput id="risk-point" className="tnum" value={typed} placeholder="-0.000000, 0.000000" onChange={(e) => setTyped(e.target.value)} />
                      </Field>
                    </FormRow>
                    {draft.shape === 'polygon' ? (
                      <Text as="p" role="caption" tone="secondary">{t('risk.form.points', { value: draft.ring.length })}{draft.ring.length > 0 ? ` — ${draft.ring.map((p) => `${p.latitude}, ${p.longitude}`).join(' · ')}` : ''}</Text>
                    ) : (
                      <>
                        {draft.center !== undefined && <Text as="p" role="caption" tone="secondary" mono>{draft.center.latitude}, {draft.center.longitude}</Text>}
                        <Field label={t('risk.form.radius')} htmlFor="risk-radius">
                          <TextInput id="risk-radius" inputMode="numeric" value={draft.radiusMeters} onChange={(e) => set('radiusMeters', e.target.value.replace(/[^\d]/g, ''))} className="tnum vello-ab-w-sm" />
                        </Field>
                      </>
                    )}
                  </Stack>

                  <Cluster gap={4} wrap>
                    <Field label={t('risk.form.from')} htmlFor="risk-from" hint={t('risk.form.zoneHint', { value: timeZone ?? 'UTC' })}>
                      <TextInput id="risk-from" className="tnum" type="datetime-local" value={draft.from} onChange={(e) => set('from', e.target.value)} />
                    </Field>
                    <Field label={t('risk.form.to')} htmlFor="risk-to" hint={t('risk.form.toHint')}>
                      <TextInput id="risk-to" className="tnum" type="datetime-local" value={draft.to} onChange={(e) => set('to', e.target.value)} />
                    </Field>
                  </Cluster>

                  <Stack as="fieldset" gap={2}>
                    <Text as="legend" role="caption" tone="secondary">{t('risk.form.daily')}</Text>
                    <Cluster gap={2} wrap>
                      {SCHEDULE_DAYS.map((day) => (
                        <CheckRow key={day} id={`risk-day-${day}`} checked={draft.daysOpen.includes(day)} onChange={(on) => set('daysOpen', on ? [...draft.daysOpen, day] : draft.daysOpen.filter((d) => d !== day))}>
                          {v('time.weekday', day)}
                        </CheckRow>
                      ))}
                    </Cluster>
                    <Cluster gap={4} wrap>
                      <Field label={t('risk.form.dailyStart')} htmlFor="risk-daily-start">
                        <TextInput id="risk-daily-start" className="tnum" type="time" value={draft.dailyStart} onChange={(e) => set('dailyStart', e.target.value)} />
                      </Field>
                      <Field label={t('risk.form.dailyEnd')} htmlFor="risk-daily-end">
                        <TextInput id="risk-daily-end" className="tnum" type="time" value={draft.dailyEnd} onChange={(e) => set('dailyEnd', e.target.value)} />
                      </Field>
                    </Cluster>
                    <Text as="p" role="caption" tone="secondary">{t('risk.form.dailyHint')}</Text>
                  </Stack>

                  <Field label={t('risk.form.categories')} htmlFor="risk-categories" hint={t('risk.form.categoriesHint')}>
                    <TextInput id="risk-categories" value={draft.categories} onChange={(e) => set('categories', e.target.value)} />
                  </Field>

                  <CheckRow id="risk-active" checked={draft.active} onChange={(on) => set('active', on)}>{t('risk.form.active')}</CheckRow>

                  {issueText !== undefined && <Notice tone="danger">{issueText}</Notice>}
                  <Cluster gap={3}>
                    <Button variant="primary" disabled={busy} onClick={() => void propose()}>{t('risk.action.propose')}</Button>
                    <Button variant="ghost" disabled={busy} onClick={() => { setDraft(EMPTY_DRAFT); setFormIssue(undefined); }}>{t('risk.action.clear')}</Button>
                  </Cluster>
                </Stack></CapWidth>
              </Stack>
            </Panel>
          )}
        </>
      )}
      <Drawer open={drawerOpen} onClose={closeDrawer} labelledBy="risk-drawer-title">
        {drawerRow !== undefined && (() => {
          const rec = drawerRow.pending ?? drawerRow.approved ?? drawerRow.latest;
          const def = rec.definition;
          const geo = def.geometry as RiskAreaGeometryView;
          const pending = drawerRow.pending;
          const block = pending !== undefined ? decideBlock(pending, me?.id, authority.mayManage, selfApproval) : undefined;
          const askingReject = rejecting !== undefined && pending !== undefined && rejecting.definition.version === pending.definition.version;
          const hours = def.schedule?.windows ?? [];
          const outline = outlineOf(def.geometry);
          return (
            <>
              <DrawerHeader
                titleId="risk-drawer-title"
                title={def.description}
                actions={
                  <Cluster gap={2} wrap>
                    {drawerRow.approved !== undefined ? (
                      <Badge tone={areaInForce(drawerRow, nowIso) ? 'success' : 'neutral'}>{t(areaInForce(drawerRow, nowIso) ? 'risk.state.inForce' : drawerRow.approved.definition.active ? 'risk.state.outOfWindow' : 'risk.state.off')}</Badge>
                    ) : (
                      <Badge tone="neutral">{t('risk.state.notApproved')}</Badge>
                    )}
                    {pending !== undefined && <Badge tone={riskStatusTone('PROPOSED')}>{t('risk.state.pending', { value: pending.definition.version })}</Badge>}
                    {pending === undefined && drawerRow.latest.status === 'REJECTED' && <Badge tone={riskStatusTone('REJECTED')}>{t('risk.state.rejected', { value: drawerRow.latest.definition.version })}</Badge>}
                  </Cluster>
                }
                onClose={closeDrawer}
                closeLabel={t('ui.close')}
              />
              <DrawerBody>
                <DrawerStack>
                  <KeyValueList
                    items={[
                      { label: t('risk.drawer.shape'), value: v('geo.shape_kind', geo.kind === 'circle' ? 'circle' : 'polygon') },
                      ...(geo.kind === 'circle'
                        ? [{ label: t('risk.form.radius'), value: String(geo.radiusMeters), mono: true }]
                        : geo.kind === 'polygon'
                          ? [{ label: t('risk.form.points'), value: String(geo.ring.length), mono: true }]
                          : []),
                      { label: t('risk.col.window'), value: `${formatMarketInstant(def.effectiveFrom, timeZone, lang)} → ${def.effectiveTo !== undefined ? formatMarketInstant(def.effectiveTo, timeZone, lang) : t('risk.openEnded')}`, mono: true },
                      {
                        label: t('risk.drawer.hours'),
                        value: hours.length === 0 ? t('risk.drawer.always') : hours.map((w) => `${w.days.map((d) => v('time.weekday', d)).join(', ')} ${w.start}–${w.end}`).join(' · '),
                      },
                      { label: t('risk.drawer.version'), value: `${def.version} / ${drawerRow.versions}`, mono: true },
                      { label: t('risk.drawer.proposedBy'), value: <PersonName id={rec.proposedBy} /> },
                      { label: t('risk.drawer.proposedAt'), value: formatMarketInstant(rec.proposedAt, timeZone, lang), mono: true },
                      { label: t('risk.drawer.fourEyes'), value: t(`risk.drawer.fourEyes.${rec.status}`) },
                      ...(rec.decidedBy !== undefined ? [{ label: t('risk.drawer.decidedBy'), value: <><PersonName id={rec.decidedBy} /><SelfApprovalNote record={rec} /></> }] : []),
                      ...(rec.decidedAt !== undefined ? [{ label: t('risk.drawer.decidedAt'), value: formatMarketInstant(rec.decidedAt, timeZone, lang), mono: true }] : []),
                      ...(rec.decisionReason !== undefined ? [{ label: t('risk.drawer.reason'), value: rec.decisionReason }] : []),
                      { label: t('risk.drawer.id'), value: <IdRef value={drawerRow.areaId} copy full />, mono: true },
                    ]}
                  />
                  {config !== undefined && outline.length > 0 && (
                    <MapCanvas
                      tiles={tileOverride(config.tiles)}
                      center={config.market.center}
                      points={[]}
                      areas={[{ id: drawerRow.areaId, points: outline, colorVar: 'var(--color-warning)', label: def.description }]}
                      focus={{ key: `${drawerRow.areaId}:${def.version}`, points: outline }}
                      ariaLabel={t('risk.map.label')}
                      pointLabel={() => ''}
                    />
                  )}
                  {askingReject && (
                    <Field label={t('risk.reject.reason')} htmlFor="risk-reason" hint={t('risk.reject.hint')}>
                      <TextArea id="risk-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                  )}
                  {pending !== undefined && block === 'OWN_PROPOSAL' && <Notice tone="neutral">{t('risk.waitOther')}</Notice>}
                </DrawerStack>
              </DrawerBody>
              {askingReject && pending !== undefined ? (
                <DrawerFooter>
                  <Button variant="secondary" disabled={busy || reason.trim() === ''} onClick={() => void decide(pending, 'REJECT')}>{t('risk.action.reject')}</Button>
                  <Button variant="ghost" disabled={busy} onClick={() => { setRejecting(undefined); setReason(''); }}>{t('ui.cancel')}</Button>
                </DrawerFooter>
              ) : (
                ((authority.mayManage && pending === undefined) || (pending !== undefined && block === undefined)) && (
                  <DrawerFooter>
                    {authority.mayManage && pending === undefined && <Button variant="secondary" disabled={busy} onClick={() => edit(drawerRow)}>{t('risk.action.edit')}</Button>}
                    {pending !== undefined && block === undefined && (
                      <>
                        <Button variant="secondary" disabled={busy} onClick={() => void decide(pending, 'APPROVE')}>{t('risk.action.approve')}</Button>
                        <Button variant="ghost" disabled={busy} onClick={() => startReject(drawerRow.areaId, pending)}>{t('risk.action.reject')}</Button>
                      </>
                    )}
                  </DrawerFooter>
                )
              )}
            </>
          );
        })()}
      </Drawer>
    </>
  );
}
